import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUpRight } from 'react-icons/fi';
import Navbar from '../Nav.jsx';
import './Main.css';
import githubLogo from '../../assets/github-mark-white.png';
import linkedinLogo from '../../assets/LI-In-Bug.png';
import emailLogo from '../../assets/icons8-mail-100.png';

const SPOTIFY_LAST_PLAYED_URL = process.env.REACT_APP_SPOTIFY_LAST_PLAYED_URL || '/api/spotify-last-played';

function HomePage() {
  const [lastPlayedArtist, setLastPlayedArtist] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadLastPlayedArtist() {
      try {
        const response = await fetch(SPOTIFY_LAST_PLAYED_URL);

        if (!response.ok) {
          return;
        }

        const data = await response.json();

        if (isMounted && data.artist) {
          setLastPlayedArtist(data.artist);
        }
      } catch (error) {
        console.error('Unable to load Spotify last played artist:', error);
      }
    }

    loadLastPlayedArtist();

    return () => {
      isMounted = false;
    };
  }, []);

  const highlights = [
    {
      title: 'Top 8 Canada — RBC x UNICEF',
      org: 'Borealis AI track',
      punch: 'Recall-first calamity prediction.',
      tags: ['Research']
    },
    {
      title: 'Reinforcement Learning locomotion tasks (35% / 80%)',
      org: 'UAlberta x MELCO',
      punch: 'Bayesian rewards + CUDA PPO acceleration.',
      tags: ['Research']
    },
    {
      title: 'AWS Wildfire — 3rd place',
      org: 'Hackathon',
      punch: 'Applied ML + RAG validation for disaster data (Bedrock, SageMaker).',
      tags: ['Applied ML']
    },
    {
      title: 'Technical lead',
      org: 'Blueprint, UAlberta',
      punch: 'React/Node, Netlify CI/CD, Firebase auth; led student team.',
      tags: ['Pro-bono-tech']
    }
  ];

  return (
    <div className="page">
      <Navbar />
      <main className="home">
        <section className="hero">
          <div className="hero__content">
            <p className="hero__eyebrow">Currently: TA @ UAlberta</p>
            <h1 className="hero__heading">Afraz Khan</h1>
            <p className="hero__title">Last played: {lastPlayedArtist || 'Don Toliver'}.</p>
            <p className="hero__summary">
              Building on RL reward shaping and CUDA-accelerated training loops, shipping GenAI demos, and hardening APIs with observability.
            </p>
            <div className="hero__actions">
              <Link to="/projects" className="btn btn-primary">
                View Projects
                <FiArrowUpRight aria-hidden="true" />
              </Link>
              <a href="#contact" className="btn btn-ghost">
                Contact Me
              </a>
            </div>
            <div className="hero__chips">
              <span className="chip">UAlberta</span>
              <span className="chip">Alberta Machine Intelligence Institute</span>
              <span className="chip">Mitsubishi Electric</span>
            </div>
          </div>

          <div className="hero__visual">
            <div className="hero__mesh" aria-hidden="true" />
            <div className="hero__photo">
              <img src={require('../../assets/profile_pic.jpg')} alt="Afraz Khan" />
              <div className="hero__photo-glow" aria-hidden="true" />
            </div>
          </div>
        </section>

        <section className="focus">
          <div className="focus__header">
            <p className="section__label">Focus areas</p>
            <h2 className="section__title">What I’m building right now</h2>
          </div>
            <div className="focus__grid">
              <div className="focus__card">
              <div className="focus__tag focus__tag--amber">Research</div>
              <h3>Hurricane prediction | RL locomotion</h3>
              <p>Recall-first hurricane models with Borealis/UNICEF; RL locomotion rewards with CUDA PPO.</p>
              <ul>
                <li>RBC x UNICEF: hurricane ML (recall-first)</li>
                <li>UAlberta x MELCO: RL rewards (35% lift, 80% faster)</li>
                <li>Safety: fault-tolerant control (LLM-shaped rewards)</li>
              </ul>
            </div>
            <div className="focus__card">
              <div className="focus__tag focus__tag--blue">Applied ML / GenAI</div>
              <h3>LLMs, CNNs, RAG</h3>
              <p>Faster fine-tunes and validation for demos and hackathons.</p>
              <ul>
                <li>Cognizant: CNN/LLM demos (+15% accuracy, faster loops)</li>
                <li>AWS Wildfire (3rd): Bedrock + SageMaker RAG validation</li>
                <li>JBot: automation + scraping for 1000+ users</li>
              </ul>
            </div>
            <div className="focus__card">
              <div className="focus__tag focus__tag--systems">Systems & Products</div>
              <h3>Systems that ship</h3>
              <p>API simulators, secure auth, and mobile products that stay observable.</p>
              <ul>
                <li>CIBC: Spring simulators + SSL test automation</li>
                <li>Blueprint: React/Node, Netlify CI/CD, Firebase auth</li>
                <li>QRCheckIn / DineIn / Taskify: React Native & full-stack builds</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="work work--compact">
          <div className="work__header">
            <p className="section__label">Highlights</p>
            <h2 className="section__title">Research + engineering in one glance</h2>
          </div>
          <div className="highlight__list">
            {highlights.map((item) => {
              const tag = item.tags[0] || 'Highlight';
              const tagKey = tag.toLowerCase();
              const variants = {
                research: 'highlight__pill--research',
                'applied ml': 'highlight__pill--applied',
                systems: 'highlight__pill--systems',
                leadership: 'highlight__pill--leadership',
                product: 'highlight__pill--product',
                automation: 'highlight__pill--automation',
                backend: 'highlight__pill--backend'
              };
              const variant = variants[tagKey] || 'highlight__pill--default';
              return (
                <Link to="/projects" key={item.title} className="highlight__row">
                  <span className={`highlight__pill ${variant}`}>{tag}</span>
                  <div className="highlight__body">
                    <div className="highlight__top">
                      <h3>{item.title}</h3>
                      <FiArrowUpRight aria-hidden="true" />
                    </div>
                    <p className="highlight__org">{item.org}</p>
                    <p className="highlight__text">{item.punch}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="contact" id="contact">
          <div className="contact__header">
            <p className="section__label">Contact</p>
            <h2 className="section__title">Let’s connect</h2>
            <p className="contact__summary">I’m always up for collaborations and conversations around RL, applied ML, and systems.</p>
          </div>
          <div className="contact__links">
            <a className="contact__pill" href="mailto:afraz1@ualberta.ca">
              <img src={emailLogo} alt="Email" className="contact__icon" />
              afraz1@ualberta.ca
            </a>
            <a className="contact__pill" href="https://www.linkedin.com/in/akhan-swe" target="_blank" rel="noreferrer">
              <img src={linkedinLogo} alt="LinkedIn" className="contact__icon" />
              LinkedIn
            </a>
            <a className="contact__pill" href="https://github.com/Afraz0Khan" target="_blank" rel="noreferrer">
              <img src={githubLogo} alt="GitHub" className="contact__icon" />
              GitHub
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}

export default HomePage;
